import {
  Box,
  CircularProgress,
  CircularProgressLabel,
  Flex,
  Grid,
  Spinner,
  Text,
} from '@chakra-ui/react'
import { IResponseBase } from 'api/types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  OccurrenceByKeyOutput,
  checkIfSelected,
  countOccurrences,
  getResultsValue,
  useGetFilterType,
} from './utils'
import { Select, SelectOption } from 'components/Select'
import CardContainer from './CardContainer'
import { useTableChartFilters } from 'contexts/TableChartFilters'
import { FilterTypeKeys } from 'components/Table/AdvancedSearch/filters'
import { SingleAdvancedFilter } from 'components/Table/AdvancedSearch/useAdvancedFilters'
import { GroupBase, OptionsOrGroups } from 'react-select'
import {
  getFirstItemOfOptions,
  sortOptions,
} from 'components/Select/selectHelpers'

const KeyPieCharts = ({
  data,
  loading,
  options: rawOptions,
  title,
  defaultProperty,
  multipleSelection = false,
  filterType,
}: {
  data: IResponseBase[]
  loading: boolean
  options?: OptionsOrGroups<
    SelectOption<string>,
    GroupBase<SelectOption<string>>
  >
  defaultProperty?: string
  title: string
  multipleSelection?: boolean
  filterType?: FilterTypeKeys
}) => {
  const options = useMemo(() => sortOptions(rawOptions ?? []), [rawOptions])

  const [results, setResults] = useState<OccurrenceByKeyOutput[]>([])
  const [valueResults, setValueResults] = useState<OccurrenceByKeyOutput[]>([])
  const [expanded, setExpanded] = useState<boolean>(false)
  const [selectedProperty, setSelectedProperty] = useState<
    SelectOption<string>
  >(
    defaultProperty
      ? { value: defaultProperty, label: defaultProperty }
      : getFirstItemOfOptions(options)
  )

  const { tableFilters, addToFilters } = useTableChartFilters()

  const filterTypes = useGetFilterType(selectedProperty.value)

  const handleClick = useCallback(
    (property: string, value: string) => {
      const tempFilters: SingleAdvancedFilter<any, any>[] = [...tableFilters]
      const existingIndex = tempFilters.findIndex((f) => f.column === property)
      const newFilterObj = {
        label: value,
        value,
      }
      if (existingIndex !== -1) {
        const existingFilter = tempFilters[existingIndex]
        const valueIndex = existingFilter.filterValue.findIndex(
          (v: any) => v.value === value
        )

        if (valueIndex !== -1) {
          existingFilter.filterValue.splice(valueIndex, 1)
          if (existingFilter.filterValue.length === 0) {
            tempFilters.splice(existingIndex, 1)
          }
        } else if (multipleSelection) {
          existingFilter.filterValue.push(newFilterObj)
        } else {
          existingFilter.filterValue = [newFilterObj]
        }
      } else {
        tempFilters.push({
          column: property,
          filterValue: [newFilterObj],
          type: filterType ?? filterTypes[0].key ?? 'includesAny',
        })
      }
      addToFilters(tempFilters)
    },
    [addToFilters, filterTypes, filterType, multipleSelection, tableFilters]
  )

  useEffect(() => {
    setResults(countOccurrences(data, selectedProperty.value, true))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProperty, loading])

  useEffect(() => {
    setValueResults(countOccurrences(data, selectedProperty.value, true))
  }, [selectedProperty, data])

  return (
    <CardContainer
      className='keyPieCharts'
      loading={loading}
      expanded={expanded}
      setExpanded={setExpanded}
      height='100%'
      cardProps={{
        pt: 1,
      }}
    >
      <Flex
        alignItems={'center'}
        h='max-content'
        justifyContent={'space-between'}
      >
        <Flex flexDir={'column'}>
          <Text fontWeight='bold' mt={-2} fontSize={'xs'} color={'black'}>
            {title}
          </Text>
        </Flex>
        {options.length > 0 && (
          <Box mb={2} mr={4} w={expanded ? '200px' : '120px'}>
            <Select
              value={selectedProperty}
              isMulti={false}
              onChange={(x: any) => setSelectedProperty(x)}
              options={options}
              backgroundColor='transparent'
              fontSize={'13px'}
              noBorder
            />
          </Box>
        )}
        {loading && (
          <Spinner
            position={'absolute'}
            zIndex={20}
            top={1}
            right={1}
            ml={2}
            size={'sm'}
          />
        )}
      </Flex>
      <Box
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        w='100%'
        maxH={expanded ? '400px' : '80%'}
        overflowY={'auto'}
      >
        <Grid templateColumns={expanded ? 'repeat(6, 1fr)' : 'repeat(2, 1fr)'}>
          {results.map((result, index) => {
            const isSelected = checkIfSelected(
              tableFilters,
              selectedProperty.value,
              result.label
            )
            const { percentage } =
              getResultsValue(valueResults, result.label as string) ?? {}
            return (
              <Flex
                backgroundColor={isSelected ? 'gray.100' : 'white'}
                onClick={() =>
                  handleClick(selectedProperty.value, result.label as string)
                }
                _hover={{
                  backgroundColor: 'gray.100',
                  color: 'black',
                }}
                border={isSelected ? '1px' : 'none'}
                borderColor={'gray.200'}
                key={index}
                cursor={'pointer'}
                rounded={'lg'}
                color={isSelected ? 'black' : 'gray.500'}
                alignItems={'center'}
                gap={1}
              >
                <CircularProgress
                  size={expanded ? '84px' : '40px'}
                  value={percentage ?? 0}
                  color='green.400'
                >
                  <CircularProgressLabel>
                    {percentage ?? 0}%
                  </CircularProgressLabel>
                </CircularProgress>
                <Text fontSize={expanded ? 'sm' : 'xs'} wordBreak='break-word'>
                  {result.label}
                </Text>
              </Flex>
            )
          })}
        </Grid>
      </Box>
    </CardContainer>
  )
}

export default KeyPieCharts
